/**
 * PostHog Product Analytics Integration
 *
 * PostHog provides behavioral product analytics, funnels, and user-level
 * drill-downs. This module handles initialization and provides utility
 * functions for capturing events and identifying users.
 *
 * Set REACT_APP_POSTHOG_ENABLED=false (or 0) in .env to disable PostHog.
 * Set REACT_APP_POSTHOG_KEY to your PostHog project API key.
 *
 * @see https://posthog.com/docs/libraries/js
 */
import posthog from 'posthog-js'

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY ?? ''
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST ?? 'https://eu.i.posthog.com'

/** Disable by setting REACT_APP_POSTHOG_ENABLED=false or =0 in .env / .env.local */
const isPosthogEnabled =
  process.env.REACT_APP_POSTHOG_ENABLED !== 'false' &&
  process.env.REACT_APP_POSTHOG_ENABLED !== '0' &&
  POSTHOG_KEY.length > 0

let isInitialized = false

/**
 * Initialize PostHog
 * Should be called once on app startup (in index.tsx)
 */
export const initPosthog = (): void => {
  if (!isPosthogEnabled) return

  if (isInitialized) {
    console.warn('PostHog already initialized')
    return
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
    })
    isInitialized = true

    if (process.env.NODE_ENV === 'development') {
      console.log('PostHog initialized')
    }
  } catch (error) {
    console.error('Failed to initialize PostHog:', error)
  }
}

/**
 * Identify a user in PostHog for behavioral attribution
 * Call this after successful authentication
 *
 * @param userId - Unique user identifier
 * @param properties - Optional user properties (role, subscription, etc.)
 */
export const identifyPosthogUser = (
  userId: string,
  properties?: Record<string, unknown>,
): void => {
  if (!isPosthogEnabled) return
  if (!isInitialized) {
    console.warn('PostHog not initialized. Call initPosthog() first.')
    return
  }

  try {
    posthog.identify(userId, properties)
  } catch (error) {
    console.error('Failed to identify user in PostHog:', error)
  }
}

/**
 * Reset PostHog user identification on logout
 * Generates a new anonymous distinct ID for the next session
 */
export const resetPosthogUser = (): void => {
  if (!isPosthogEnabled) return
  if (!isInitialized) return

  try {
    posthog.reset()
  } catch (error) {
    console.error('Failed to reset PostHog user:', error)
  }
}

/**
 * Capture a custom event in PostHog
 *
 * @param eventName - Name of the event (e.g., 'new_project')
 * @param properties - Optional event properties
 */
export const capturePosthogEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
): void => {
  if (!isPosthogEnabled) return
  if (!isInitialized) {
    console.warn('PostHog not initialized. Call initPosthog() first.')
    return
  }

  try {
    posthog.capture(eventName, properties)
  } catch (error) {
    console.error('Failed to capture PostHog event:', error)
  }
}
